import { useEffect, useState } from 'react';

/**
 * True once the visitor has scrolled past `thresholdPx`, false once back above
 * `thresholdPx - bufferPx`. Row one stays sticky always; this drives the index strip's collapse
 * (TEC navbar redesign Phase 1). The two triggers must stay apart: collapsing and expanding at
 * the same position (or on scroll direction alone) let ordinary scroll jitter near that single
 * point flip `collapsed` on almost every tick, which read as the strip vibrating. Position-based
 * hysteresis makes the flip depend on distance scrolled, not direction, so a 1px wobble at rest
 * can't cross both triggers.
 */
export function useScrollCollapse(thresholdPx = 64, bufferPx = 24): boolean {
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const expandAt = Math.max(0, thresholdPx - bufferPx);
    const handleScroll = () => {
      const y = window.scrollY;
      setCollapsed((wasCollapsed) => {
        if (y > thresholdPx) return true;
        if (y < expandAt) return false;
        return wasCollapsed;
      });
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [thresholdPx, bufferPx]);

  return collapsed;
}
